import type React from "react";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import type { IAlertBlock } from "@/api/interfaces/alertLayout";
import ArrowDown from "@/assets/svg/menu/arrow-down.svg?react";
import { Icon, P } from "@/base/components/Global";
import H6 from "@/base/components/Global/H6";
import { Row } from "@/base/components/Layout";
import { createUseStyles } from "@/theme";

const TRANSFORM_DURATION = ".2s";

const useStyles = createUseStyles(({ color, spacing, borderRadius }) => ({
  alert: {
    backgroundColor: color.gray6,
    borderRadius: borderRadius.s,
    borderLeft: `4px solid ${color.primaryBrand}`,
    padding: spacing.l,
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  arrowOpen: {
    transform: "rotate(180deg)",
    transition: `all ${TRANSFORM_DURATION} ease`,
  },
  arrowClose: {
    transform: "rotate(0deg)",
    transition: `all ${TRANSFORM_DURATION} ease`,
  },
  content: {
    overflow: "hidden",
    width: "100%",
  },
  subtitle: {
    marginTop: spacing.xs,
  },
  description: {
    borderTop: `1px solid ${color.gray5}`,
    paddingTop: spacing.m,
    marginTop: spacing.m,
  },
}));

type AlertBlockProps = {
  block?: IAlertBlock;
};

export const AlertBlock: React.FC<AlertBlockProps> = ({ block }) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!block) {
    return null;
  }

  const { title, subTitle, description } = block;

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <Row>
      <div className={classes.alert}>
        {title && (
          <div onClick={toggleExpanded} className={classes.header}>
            <H6 gray1 noMarginBottom>
              {title}
            </H6>
            <Icon
              icon={<ArrowDown />}
              className={isExpanded ? classes.arrowOpen : classes.arrowClose}
            />
          </div>
        )}
        {subTitle && (
          <P gray3 small noMarginBottom className={classes.subtitle}>
            {subTitle}
          </P>
        )}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.2 }}
              className={classes.content}
            >
              <div className={classes.description}>
                <P cmsContent={description} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Row>
  );
};
